import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Checkbox,
  Flex,
  Icon,
  Link,
  Option,
  SearchInput,
  Select,
  Table,
  Text,
} from "@appsmith/ads";

import { Card, Empty, LoadingMembers, SeatStatusIndicator } from "components";
import {
  createMessage,
  MEMBERS_TEXT,
  NO_SEAT_ASSIGNED_TEXT,
  PRICING_SUMMARY_PURCHASE_SEATS,
  SEARCH_MEMBERS_PLACEHOLDER,
  SEAT_ASSIGNED_TEXT,
  SEATS_ASSIGNMENT_TEXT,
  USER_TABLE,
  USER_TABLE_FILTERS,
} from "constants/Messages";
import { DeleteMembersModal } from "./DeleteMembersModal";
import { FiltersRow } from "./MembersList.styles";
import { InviteModal } from "./InviteModal";
import {
  Member,
  MemberRoleFilter,
  MemberSortField,
  UpdateMember,
} from "types/MembersTypes";
import { TableBulkSelectHeader } from "./TableBulkSelectHeader";
import { useMembers } from "hooks";
import {
  bulkSelectMembers,
  clearMemberSelection,
  deselectMember,
  selectMember,
} from "actions/MembersActions";
import {
  getAssignSeatsLoading,
  getDeleteMembersLoading,
  getSelectedMembers,
  getUnassignSeatsLoading,
} from "selectors/MembersSelectors";

const ALL_FILTER_VALUE = "ALL";

const FILTER_SELECT_SUFFIX_ICON = <Icon name="arrow-down-s-line" size="md" />;

const MembersList = () => {
  const {
    assignSeatsToMembers,
    isSeatsFullyUtilized,
    memberList,
    membersLoading,
    observableItemRef,
    onInviteMembers,
    paginatedOccupiedSeats,
    paginatedTotalMembers,
    queryParams,
    roleFilter,
    searchTerm,
    seatAssignedFilter,
    setRoleFilter,
    setSearchTerm,
    setSeatAssignedFilter,
    showInfiniteScrollLoading,
    sortBy,
    sortOrder,
    toggleSort,
    unassignSeatsToMembers,
  } = useMembers();

  const dispatch = useDispatch();
  const selectedMembersArray = useSelector(getSelectedMembers);
  const selectedMembers = useMemo(
    () => new Set(selectedMembersArray),
    [selectedMembersArray],
  );
  const deleteMembersLoading = useSelector(getDeleteMembersLoading);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Track previous member list length and search term to detect resets vs appends
  const prevMemberListLengthRef = useRef<number>(0);
  const prevSearchTermRef = useRef<string>(searchTerm);

  // Reset select state only when:
  // 1. Search term changes (new search results)
  // 2. Member list is reset (length decreases, indicating page 0 reload)
  // NOT when infinite scroll appends items (length increases)
  useEffect(() => {
    const currentLength = memberList.length;
    const prevLength = prevMemberListLengthRef.current;
    const currentSearchTerm = searchTerm;
    const prevSearchTerm = prevSearchTermRef.current;

    // Clear selections if:
    // - Search term changed (new search)
    // - List was reset (length decreased, indicating page 0 reload)
    const shouldClear =
      currentSearchTerm !== prevSearchTerm ||
      (prevLength > 0 && currentLength < prevLength);

    if (shouldClear) {
      dispatch(clearMemberSelection());
    }

    prevMemberListLengthRef.current = currentLength;
    prevSearchTermRef.current = currentSearchTerm;
  }, [searchTerm, memberList.length, dispatch]);

  // Clear selections after seat assignment/unassignment operations complete
  const assignSeatsLoading = useSelector(getAssignSeatsLoading);
  const unassignSeatsLoading = useSelector(getUnassignSeatsLoading);
  const prevAssignLoadingRef = useRef<boolean>(false);
  const prevUnassignLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    // Clear selections when assign operation completes (loading goes from true to false)
    if (prevAssignLoadingRef.current && !assignSeatsLoading) {
      dispatch(clearMemberSelection());
    }
    prevAssignLoadingRef.current = assignSeatsLoading;
  }, [assignSeatsLoading, dispatch]);

  useEffect(() => {
    // Clear selections when unassign operation completes (loading goes from true to false)
    if (prevUnassignLoadingRef.current && !unassignSeatsLoading) {
      dispatch(clearMemberSelection());
    }
    prevUnassignLoadingRef.current = unassignSeatsLoading;
  }, [unassignSeatsLoading, dispatch]);

  const prevDeleteLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    if (prevDeleteLoadingRef.current && !deleteMembersLoading) {
      setIsDeleteModalOpen(false);
    }
    prevDeleteLoadingRef.current = deleteMembersLoading;
  }, [deleteMembersLoading]);

  const eligibleMembers = useMemo(() => memberList, [memberList]);

  // Bulk select
  const handleBulkSelect = useCallback(
    (checked: boolean) => {
      if (checked) {
        dispatch(
          bulkSelectMembers(eligibleMembers.map((member) => member.email)),
        );
      } else {
        dispatch(clearMemberSelection());
      }
    },
    [dispatch, eligibleMembers],
  );

  // Individual select
  const handleMemberSelect = useCallback(
    (email: string, checked: boolean) => {
      dispatch(checked ? selectMember(email) : deselectMember(email));
    },
    [dispatch],
  );

  //disable unassign, if all selected memebers are already unassigned
  const isUnassignDisabled = useMemo(() => {
    if (selectedMembers.size === 0) return false;

    const allUnassigned = Array.from(selectedMembers).every(
      (email) =>
        memberList.find((member) => member.email === email)?.seatAssigned ===
        false,
    );

    return allUnassigned;
  }, [selectedMembers, memberList]);

  //disable assign, if all selected memebers are already assigned or seats are fully utilized
  const isAssignDisabled = useMemo(() => {
    if (selectedMembers.size === 0) return false;

    const allAssigned = Array.from(selectedMembers).every(
      (email) =>
        memberList.find((member) => member.email === email)?.seatAssigned ===
        true,
    );

    return allAssigned || isSeatsFullyUtilized;
  }, [selectedMembers, memberList, isSeatsFullyUtilized]);

  // Unassign seats
  const handleUnassignSeats = useCallback(() => {
    const membersToUnassign: UpdateMember[] = Array.from(selectedMembers).map(
      (email) => ({
        email,
        assignSeat: false,
      }),
    );
    unassignSeatsToMembers(membersToUnassign);
  }, [selectedMembers, unassignSeatsToMembers]);

  // Assign seats
  const handleAssignSeats = useCallback(() => {
    const membersToAssign: UpdateMember[] = Array.from(selectedMembers).map(
      (email) => ({
        email,
        assignSeat: true,
      }),
    );
    assignSeatsToMembers(membersToAssign);
  }, [selectedMembers, assignSeatsToMembers]);

  const handleDeleteMembers = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  const handleRowClick = useCallback(
    (email: string) => {
      handleMemberSelect(email, !selectedMembers.has(email));
    },
    [selectedMembers, handleMemberSelect],
  );

  // Check if all members are selected
  const isAllSelected = useMemo(
    () =>
      eligibleMembers.length > 0 &&
      selectedMembers.size === eligibleMembers.length,
    [eligibleMembers, selectedMembers],
  );

  // Check if some members are selected
  const isIndeterminate = useMemo(
    () => selectedMembers.size > 0 && selectedMembers.size < memberList.length,
    [memberList, selectedMembers],
  );

  // Clickable column header cycling ascending -> descending -> unsorted
  const renderSortableTitle = useCallback(
    (label: string, field: MemberSortField) => {
      const isActive = sortBy === field;
      return (
        <Flex
          alignItems="center"
          aria-label={
            isActive
              ? `Sort by ${label}, currently ${
                  sortOrder === "ASC" ? "ascending" : "descending"
                }`
              : `Sort by ${label}`
          }
          className="cursor-pointer select-none focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-[var(--ads-v2-color-border-emphasis-plus)]"
          gap="spaces-2"
          onClick={() => toggleSort(field)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleSort(field);
            }
          }}
          role="button"
          tabIndex={0}
        >
          {label}
          {/* Only remixicon-react-backed names render in this app; the ADS
              svg-based icons (e.g. sort-control) resolve to raw data URIs
              here and crash React.lazy, so the unsorted state shows no icon */}
          {isActive && (
            <Icon
              name={
                sortOrder === "ASC" ? "arrow-up-s-line" : "arrow-down-s-line"
              }
              size="sm"
            />
          )}
        </Flex>
      );
    },
    [sortBy, sortOrder, toggleSort],
  );

  // Columns
  const columns = useMemo(
    () => [
      {
        dataIndex: "bulk",
        render: (_: string, record: Member) => {
          return (
            <Flex height="16px">
              <Checkbox
                aria-label={record.email}
                isSelected={selectedMembers.has(record.email)}
              />
            </Flex>
          );
        },
        title: (
          <Flex height="16px">
            <Checkbox
              aria-label="Select all users"
              isIndeterminate={isIndeterminate}
              isSelected={isAllSelected}
              onChange={handleBulkSelect}
            />
          </Flex>
        ),
        width: "50px",
      },
      {
        dataIndex: "email",
        title: renderSortableTitle(createMessage(USER_TABLE.user), "EMAIL"),
      },
      {
        dataIndex: "role",
        title: renderSortableTitle(createMessage(USER_TABLE.access), "ROLE"),
      },
      {
        dataIndex: "organizationUrl",
        title: renderSortableTitle(
          createMessage(USER_TABLE.instance),
          "ORGANIZATION_URL",
        ),
        render: (organizationUrl: string) => {
          let url = organizationUrl;
          if (
            organizationUrl &&
            !url.startsWith("http://") &&
            !url.startsWith("https://")
          ) {
            url = `https://${url}`;
          }
          return organizationUrl ? <Link to={url}>{organizationUrl}</Link> : "";
        },
      },
      {
        dataIndex: "seatAssigned",
        title: renderSortableTitle(
          createMessage(USER_TABLE.status),
          "SEAT_ASSIGNED",
        ),
        render: (seatAssigned: boolean) => (
          <SeatStatusIndicator seatAssigned={seatAssigned} />
        ),
      },
    ],
    [
      handleBulkSelect,
      handleMemberSelect,
      isAllSelected,
      isIndeterminate,
      renderSortableTitle,
      selectedMembers,
    ],
  );

  return (
    <Card flexDirection="column" gap="spaces-7" overflow="hidden">
      {/* Header */}
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <Text kind="heading-m">{createMessage(MEMBERS_TEXT)}</Text>
        <InviteModal onInviteMembers={onInviteMembers} />
      </Flex>

      {/* Members */}
      <Flex flexDirection="column" gap="spaces-4" overflow="hidden">
        <Flex alignItems="center" justifyContent="space-between" width="100%">
          <FiltersRow>
            <SearchInput
              className="!w-[206px]"
              onChange={(e) => setSearchTerm(e)}
              placeholder={createMessage(SEARCH_MEMBERS_PLACEHOLDER)}
              size="md"
              value={searchTerm}
            />
            <Select
              aria-label="Filter by access"
              className="!w-[180px]"
              onSelect={(value: string) =>
                setRoleFilter(
                  value === ALL_FILTER_VALUE
                    ? undefined
                    : (value as MemberRoleFilter),
                )
              }
              size="md"
              suffixIcon={FILTER_SELECT_SUFFIX_ICON}
              value={roleFilter ?? ALL_FILTER_VALUE}
            >
              <Option value={ALL_FILTER_VALUE}>
                {createMessage(USER_TABLE_FILTERS.all_access)}
              </Option>
              <Option value="ADMIN">
                {createMessage(USER_TABLE_FILTERS.admin)}
              </Option>
              <Option value="MEMBER">
                {createMessage(USER_TABLE_FILTERS.member)}
              </Option>
            </Select>
            <Select
              aria-label="Filter by status"
              className="!w-[180px]"
              onSelect={(value: string) =>
                setSeatAssignedFilter(
                  value === ALL_FILTER_VALUE ? undefined : value === "true",
                )
              }
              size="md"
              suffixIcon={FILTER_SELECT_SUFFIX_ICON}
              value={
                seatAssignedFilter === undefined
                  ? ALL_FILTER_VALUE
                  : String(seatAssignedFilter)
              }
            >
              <Option value={ALL_FILTER_VALUE}>
                {createMessage(USER_TABLE_FILTERS.all_statuses)}
              </Option>
              <Option value="true">{createMessage(SEAT_ASSIGNED_TEXT)}</Option>
              <Option value="false">
                {createMessage(NO_SEAT_ASSIGNED_TEXT)}
              </Option>
            </Select>
          </FiltersRow>
          <Text kind="body-m">
            {createMessage(
              SEATS_ASSIGNMENT_TEXT,
              paginatedOccupiedSeats,
              paginatedTotalMembers,
            )}
          </Text>
        </Flex>

        <Flex
          border="1px solid var(--ads-v2-color-border)"
          borderRadius="var(--ads-v2-border-radius)"
          flexDirection="column"
          overflowY="auto"
          position={"relative"}
          width="100%"
        >
          <TableBulkSelectHeader
            handleAssignSeats={handleAssignSeats}
            handleDeleteMembers={handleDeleteMembers}
            handleUnassignSeats={handleUnassignSeats}
            isAllSelected={isAllSelected}
            isAssignDisabled={isAssignDisabled}
            isIndeterminate={isIndeterminate}
            isSeatsFullyUtilized={isSeatsFullyUtilized}
            isUnassignDisabled={isUnassignDisabled}
            onBulkSelect={handleBulkSelect}
            selectedCount={selectedMembers.size}
          />
          <Table
            columns={columns}
            data={membersLoading ? [] : memberList}
            emptyText={
              membersLoading ? (
                <LoadingMembers />
              ) : (
                <Empty
                  image={"https://assets.appsmith.com/Nomember.svg"}
                  text={createMessage(
                    PRICING_SUMMARY_PURCHASE_SEATS.members_empty_state,
                  )}
                />
              )
            }
            onRow={(record) => ({
              onClick: () => handleRowClick(record.email),
              className: "cursor-pointer",
            })}
            rowHoverable
            sticky
          />
          {showInfiniteScrollLoading && (
            <div
              className="flex justify-center items-center p-[var(--ads-v2-spaces-6)] w-[100%]"
              ref={observableItemRef}
            >
              <LoadingMembers />
            </div>
          )}
        </Flex>
      </Flex>
      <DeleteMembersModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        queryParams={queryParams}
      />
    </Card>
  );
};

export { MembersList };
